// Fiyat Teklifi PDF üreteci — kaydedilen bir teklifi firma başlıklı, müşteri odaklı
// tek sayfa PDF olarak indirir. companyProfilePdf.js ile aynı görsel dili paylaşır
// (logo + üst şerit + Roboto TTF Türkçe destek).
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { registerTurkishFont } from "@/lib/pdfFont";

async function loadLogoDataUrl() {
  try {
    const res = await fetch("/plasper-logo.jpg");
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
  } catch { return null; }
}

const fmtTRY = (v) =>
  new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 2 })
    .format(Number(v || 0));

const fmtTRLong = (iso) => {
  if (!iso) return "";
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" });
  }
  return String(iso);
};

const safeFileName = (s) =>
  String(s || "teklif")
    .replace(/[^a-zA-Z0-9-_ğüşıöçİĞÜŞÖÇ ]/g, "")
    .trim()
    .replace(/\s+/g, "_") || "teklif";

/**
 * Options:
 *   validityDays: geçerlilik gün sayısı (varsayılan 15)
 *   saveMode: "save" (varsayılan; doc.save) | "blob" (döndürür); WhatsApp/e-posta paylaşımı için blob
 */
export async function generateQuotePDF(quote, contact, company, opts = {}) {
  const { validityDays = 15, saveMode = "save" } = opts;
  const doc = new jsPDF({ compress: true });
  await registerTurkishFont(doc);
  const logo = await loadLogoDataUrl();
  const w = doc.internal.pageSize.getWidth();
  const marginX = 14;

  // -------- Üst şerit: logo + firma bilgileri + TEKLİF etiketi
  if (logo) {
    try { doc.addImage(logo, "JPEG", marginX, 10, 42, 14, undefined, "FAST"); } catch {}
  }

  // Firma başlık (logo altında)
  doc.setFont(undefined, "bold");
  doc.setFontSize(10);
  const companyName = company?.name || "—";
  const nameLines = doc.splitTextToSize(companyName, 110);
  let cy = 30;
  nameLines.forEach((ln) => { doc.text(ln, marginX, cy); cy += 4.5; });
  doc.setFont(undefined, "normal");
  doc.setFontSize(8);
  doc.setTextColor(100);
  if (company?.address) doc.text(doc.splitTextToSize(company.address, 110), marginX, cy);
  cy += doc.splitTextToSize(company?.address || "", 110).length * 3.5 + 1;
  const contactLine = [company?.phone, company?.email].filter(Boolean).join("  ·  ");
  if (contactLine) { doc.text(contactLine, marginX, cy); cy += 4; }
  doc.setTextColor(0);

  // Sağ üst: TEKLİF başlığı
  doc.setFont(undefined, "bold");
  doc.setFontSize(20);
  doc.text("TEKLİF", w - marginX, 20, { align: "right" });
  doc.setFont(undefined, "normal");
  doc.setFontSize(9);
  doc.setTextColor(80);
  doc.text(`No: ${String(quote.id || "").slice(-8).toUpperCase()}`, w - marginX, 26, { align: "right" });
  doc.text(`Tarih: ${fmtTRLong(quote.date)}`, w - marginX, 31, { align: "right" });
  // Geçerlilik: teklif tarihinden validityDays gün sonrası
  let validUntil = "";
  const m = String(quote.date || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    d.setDate(d.getDate() + validityDays);
    validUntil = d.toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" });
    doc.text(`Geçerlilik: ${validUntil} (${validityDays} gün)`, w - marginX, 36, { align: "right" });
  }
  doc.setTextColor(0);

  // Ayraç
  const dividerY = Math.max(cy + 3, 46);
  doc.setLineWidth(0.4);
  doc.line(marginX, dividerY, w - marginX, dividerY);

  // -------- Müşteri kutusu
  let y = dividerY + 6;
  doc.setFontSize(9);
  doc.setTextColor(120);
  doc.text("SAYIN", marginX, y);
  doc.setTextColor(0);
  doc.setFont(undefined, "bold");
  doc.setFontSize(12);
  doc.text(contact?.name || "—", marginX, y + 5);
  doc.setFont(undefined, "normal");
  doc.setFontSize(9);
  let cby = y + 10;
  const custLines = [
    contact?.address,
    [contact?.district, contact?.province].filter(Boolean).join(" / "),
    contact?.tax_id ? `VKN/TCKN: ${contact.tax_id}` : null,
    [contact?.phone, contact?.email].filter(Boolean).join("  ·  "),
  ].filter(Boolean);
  doc.setTextColor(60);
  custLines.forEach((ln) => {
    doc.splitTextToSize(String(ln), w - marginX * 2).forEach((sub) => {
      doc.text(sub, marginX, cby);
      cby += 4;
    });
  });
  doc.setTextColor(0);

  // -------- Teklif detay tablosu — items[] varsa çok satırlı, yoksa tek satır
  const tableStart = cby + 4;
  const rows = [];
  const hasItems = Array.isArray(quote.items) && quote.items.length > 0;

  if (hasItems) {
    quote.items.forEach((it, i) => {
      const boxLabel = it.box_type === "plaka" ? "Düz Plaka"
                    : it.box_type === "half_lid" ? "Yarım Kapak"
                    : it.box_type === "abox" ? "Amerikan Kutu (A Box)" : "Karton kutu";
      // Ürün adı en üstte kalın; teknik bilgi ikinci satırda küçük
      const productName = (it.title && String(it.title).trim())
                         || (it.stock_name && String(it.stock_name).trim())
                         || boxLabel;
      const specLine = [
        productName === boxLabel ? null : boxLabel,
        it.flute && it.quality ? `${it.flute} · ${it.quality}` : null,
        it.stock_sku ? `[${it.stock_sku}]` : null,
      ].filter(Boolean).join(" · ");
      const desc = specLine ? `${productName}\n${specLine}` : productName;
      const dims = it.box_type === "plaka"
        ? (it.sheet_width_mm && it.sheet_length_mm ? `${it.sheet_width_mm}×${it.sheet_length_mm} mm` : "")
        : (it.length_mm != null ? `${it.length_mm}×${it.width_mm}×${it.height_mm} mm` : "");
      const q = Number(it.quantity || 0);
      const unitPrice = q > 0 ? Number(it.karton_total || 0) / q : 0;
      rows.push([
        String(i + 1),
        desc,
        dims,
        q.toLocaleString("tr-TR"),
        fmtTRY(unitPrice),
        fmtTRY(it.karton_total),
      ]);
    });
  } else {
    const boxLabel = quote.box_type === "abox" ? "Amerikan Kutu (A Box)"
                   : quote.box_type === "plaka" ? "Düz Plaka"
                   : quote.box_type === "half_lid" ? "Yarım Kapak" : "Kutu";
    const productName = (quote.title && String(quote.title).trim())
                       || (quote.stock_name && String(quote.stock_name).trim())
                       || boxLabel;
    const specLine = [
      productName === boxLabel ? null : boxLabel,
      quote.flute && quote.quality ? `${quote.flute} · ${quote.quality}` : null,
      quote.stock_sku ? `[${quote.stock_sku}]` : null,
    ].filter(Boolean).join(" · ");
    const boxDesc = specLine ? `${productName}\n${specLine}` : productName;
    const kartonPerUnit = quote.quantity > 0 ? Number(quote.karton_total || 0) / quote.quantity : 0;
    const dims = quote.box_type === "plaka"
      ? (quote.sheet_width_mm && quote.sheet_length_mm ? `${quote.sheet_width_mm}×${quote.sheet_length_mm} mm` : "")
      : (quote.length_mm != null ? `${quote.length_mm}×${quote.width_mm}×${quote.height_mm} mm` : "");
    rows.push([
      "1",
      boxDesc,
      dims,
      Number(quote.quantity || 0).toLocaleString("tr-TR"),
      fmtTRY(kartonPerUnit),
      fmtTRY(quote.karton_total),
    ]);
  }
  // Nakliye satırları (her iki modda da alt satırlar olarak)
  (quote.shipping_lines || []).forEach((s, i) => {
    rows.push([
      String(rows.length + 1),
      `Nakliye · ${s.vehicle_name || ""}`.trim(),
      `${s.from_location || ""} → ${s.to_location || ""}${s.return_trip ? " (gidiş-dönüş)" : ""}`,
      `${s.distance_km || ""} km`,
      "—",
      fmtTRY(s.total_cost),
    ]);
  });

  autoTable(doc, {
    startY: tableStart,
    head: [["#", "Açıklama", "Ölçü / Güzergâh", "Adet", "Kutu Başı", "Tutar"]],
    body: rows,
    styles: { fontSize: 9, font: "Roboto", cellPadding: 2.8 },
    headStyles: {
      fillColor: [10, 10, 10],
      font: "Roboto", fontStyle: "bold",
      halign: "left", textColor: 255,
    },
    columnStyles: {
      0: { cellWidth: 8, halign: "center" },
      1: { cellWidth: 55, fontStyle: "bold" },
      2: { cellWidth: 55 },
      3: { cellWidth: 22, halign: "right", font: "Roboto" },
      4: { cellWidth: 22, halign: "right", font: "Roboto" },
      5: { cellWidth: "auto", halign: "right", font: "Roboto", fontStyle: "bold" },
    },
    didParseCell: (data) => {
      // Açıklama hücresinde "\n"'den sonraki satır (spec) daha küçük ve normal ağırlıkta olsun
      if (data.section === "body" && data.column.index === 1 && typeof data.cell.raw === "string" && data.cell.raw.includes("\n")) {
        const [head, ...rest] = data.cell.raw.split("\n");
        data.cell.text = [head, rest.join(" · ")];
      }
    },
    theme: "grid",
  });

  // -------- Toplam kutu (Nakliye satırı kaldırıldı; Nakliye "Ek Kalem" tablosunda gösteriliyor)
  const totalY = doc.lastAutoTable.finalY + 4;
  const totalBoxX = w - marginX - 80;
  const vatRate = Number(quote.vat_rate || 0);
  const grandNet = Number(quote.grand_total || 0);
  const vatAmount = grandNet * (vatRate / 100);
  const grandWithVat = grandNet + vatAmount;
  const hasVat = vatRate > 0;
  const boxHeight = hasVat ? 29 : 17;
  doc.setDrawColor(10);
  doc.setLineWidth(0.6);
  doc.rect(totalBoxX, totalY, 80, boxHeight);
  doc.setFontSize(9);
  doc.setTextColor(90);
  doc.text("Ara Toplam (Karton)", totalBoxX + 2, totalY + 5);
  doc.setFont(undefined, "bold");
  doc.setTextColor(0);
  doc.text(hasVat ? "GENEL TOPLAM (KDV Hariç)" : "GENEL TOPLAM", totalBoxX + 2, totalY + 12);
  doc.setFont(undefined, "normal");
  doc.setTextColor(0);
  doc.setFontSize(9);
  doc.text(fmtTRY(quote.karton_total), totalBoxX + 78, totalY + 5, { align: "right" });
  doc.setFont(undefined, "bold");
  doc.setFontSize(hasVat ? 10 : 11);
  doc.text(fmtTRY(grandNet), totalBoxX + 78, totalY + 12, { align: "right" });
  if (hasVat) {
    doc.setFont(undefined, "normal");
    doc.setFontSize(9);
    doc.setTextColor(90);
    doc.text(`KDV %${vatRate.toLocaleString("tr-TR")}`, totalBoxX + 2, totalY + 18);
    doc.setTextColor(0);
    doc.text(fmtTRY(vatAmount), totalBoxX + 78, totalY + 18, { align: "right" });
    doc.setFont(undefined, "bold");
    doc.setFontSize(11);
    doc.text("KDV DAHİL TOPLAM", totalBoxX + 2, totalY + 25);
    doc.text(fmtTRY(grandWithVat), totalBoxX + 78, totalY + 25, { align: "right" });
  }
  doc.setFont(undefined, "normal");

  // Kutu başı satış (bilgi) — toplam kutu artık daha kısa (17 mm veya 29 mm), yazıyı ona göre hizala
  doc.setFontSize(8);
  doc.setTextColor(90);
  const perUnitSale = quote.quantity > 0 ? Number(quote.grand_total || 0) / quote.quantity : 0;
  doc.text(
    `Kutu başı: ${fmtTRY(perUnitSale)}  ·  Adet: ${Number(quote.quantity || 0).toLocaleString("tr-TR")}`,
    marginX, totalY + 12
  );
  doc.setTextColor(0);

  // -------- Ekstra Bedeller (Kalıp / Klişe / Nakliye / Palet) — Notlar üstünde
  const extraRows = [];
  const _add = (label, value, note) => {
    const n = Number(value || 0);
    if (n > 0) extraRows.push([label, fmtTRY(n), note || ""]);
  };
  _add("Kalıp Bedeli", quote.mold_cost);
  _add("Klişe Bedeli", quote.cliche_cost);
  _add("Nakliye Bedeli", quote.extra_shipping_cost);
  if (Number(quote.pallet_cost_per_unit || 0) > 0) {
    // Kullanıcı isteği: palet bedeli KUTU SAYISI ile ÇARPILMAZ — 1 paletin birim fiyatı yazılır.
    extraRows.push([
      `Palet Bedeli${quote.pallet_deposit === false ? "" : " — DEPOZİTOLU"}`,
      fmtTRY(quote.pallet_cost_per_unit),
      quote.pallet_deposit === false ? "1 palet birim fiyatı" : "1 palet birim fiyatı (depozitolu)",
    ]);
  }

  let extrasFinalY = doc.lastAutoTable.finalY + (hasVat ? 12 : 0);
  if (extraRows.length > 0) {
    autoTable(doc, {
      startY: extrasFinalY + 22,   // Toplam kutusunun altından başla
      head: [["Ek Kalem", "Tutar", "Açıklama"]],
      body: extraRows,
      styles: { fontSize: 9, font: "Roboto", cellPadding: 2.8 },
      headStyles: {
        fillColor: [40, 40, 40],
        font: "Roboto", fontStyle: "bold",
        halign: "left", textColor: 255,
      },
      columnStyles: {
        0: { cellWidth: 70 },
        1: { cellWidth: 40, halign: "right", font: "Roboto", fontStyle: "bold" },
        2: { cellWidth: "auto" },
      },
      theme: "grid",
    });
    extrasFinalY = doc.lastAutoTable.finalY;
  }

  // -------- Notlar & şartlar
  const notesY = (extraRows.length > 0 ? extrasFinalY + 8 : totalY + 25);
  doc.setFontSize(9);
  doc.setFont(undefined, "bold");
  doc.text("Notlar & Şartlar", marginX, notesY);
  doc.setFont(undefined, "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(70);
  const notes = [
    `• Fiyatlar KDV ${hasVat ? `dahil değildir (KDV %${vatRate.toLocaleString("tr-TR")}).` : "hariçtir."}`,
    `• Teklif geçerliliği ${validityDays} gündür${validUntil ? ` (son gün: ${validUntil})` : ""}.`,
    "• Nakliye ücreti aynı adres için sabittir; adres değişiminde tekrar hesaplanır.",
  ];
  // Ödeme koşulları — dolu olan alanlar (avans / peşinat / vade) tek satırda özetlenir
  const termParts = [];
  if (Number(quote.advance_payment_pct || 0) > 0) termParts.push(`sipariş anında %${Number(quote.advance_payment_pct).toLocaleString("tr-TR")} avans`);
  if (Number(quote.down_payment_pct || 0) > 0) termParts.push(`teslim öncesi %${Number(quote.down_payment_pct).toLocaleString("tr-TR")} peşinat`);
  const vd = Number(quote.payment_term_days || 0);
  if (vd > 0) termParts.push(`kalan ${vd} gün vadeli`);
  if (termParts.length) {
    notes.push(`• Ödeme koşulları: ${termParts.join(", ")}.`);
  } else {
    notes.push("• Termin ve ödeme koşulları sipariş onayı ile birlikte netleşir.");
  }
  if (quote.notes) notes.push(`• ${quote.notes}`);
  let ny = notesY + 4;
  notes.forEach((n) => {
    doc.splitTextToSize(n, w - marginX * 2).forEach((sub) => {
      doc.text(sub, marginX, ny);
      ny += 4;
    });
  });
  doc.setTextColor(0);

  // -------- Banka & imza
  if (company?.iban || company?.bank) {
    ny += 4;
    doc.setFont(undefined, "bold");
    doc.setFontSize(9);
    doc.text("Banka Bilgileri", marginX, ny);
    doc.setFont(undefined, "normal");
    doc.setFontSize(9);
    ny += 4;
    if (company?.bank) { doc.text(`Banka: ${company.bank}`, marginX, ny); ny += 4; }
    if (company?.iban) { doc.text(`IBAN: ${company.iban}`, marginX, ny); ny += 4; }
  }

  // İmza / kaşe (sağ alt)
  const pageH = doc.internal.pageSize.getHeight();
  doc.setLineWidth(0.3);
  doc.line(w - marginX - 60, pageH - 22, w - marginX, pageH - 22);
  doc.setFontSize(8);
  doc.setTextColor(90);
  doc.text("Yetkili İmza / Kaşe", w - marginX - 30, pageH - 17, { align: "center" });
  doc.setTextColor(0);

  // Alt bilgi
  doc.setFontSize(7);
  doc.setTextColor(120);
  doc.text(
    `Bu belge Plasper Finans tarafından ${new Date().toLocaleDateString("tr-TR")} tarihinde üretilmiştir.`,
    marginX, pageH - 8
  );
  doc.setTextColor(0);

  const fname = `teklif_${safeFileName(contact?.name)}_${quote.date || ""}.pdf`;
  if (saveMode === "blob") return { blob: doc.output("blob"), filename: fname };
  doc.save(fname);
  return { filename: fname };
}

/** Teklif özetini WhatsApp/e-posta gövdesi için düz metne çevirir. */
export function buildQuoteShareText(quote, contact, company) {
  const lines = [];
  lines.push(`Sayın ${contact?.name || ""},`);
  lines.push("");
  lines.push(`${fmtTRLong(quote.date)} tarihli fiyat teklifimiz:`);
  if (quote.length_mm != null) {
    lines.push(
      `• Kutu: ${quote.length_mm}×${quote.width_mm}×${quote.height_mm} mm  ×  ${Number(quote.quantity || 0).toLocaleString("tr-TR")} adet`
    );
  }
  if (quote.flute && quote.quality) lines.push(`• Karton: ${quote.flute} · ${quote.quality}`);
  lines.push(`• Karton Toplam: ${fmtTRY(quote.karton_total)}`);
  if (Number(quote.shipping_total || 0) > 0) lines.push(`• Nakliye (Ana tablo): ${fmtTRY(quote.shipping_total)}`);
  // Ekstra bedeller — sadece dolu olanlar (Nakliye Bedeli burada, ek kalemler kısmında)
  const extras = [];
  if (Number(quote.mold_cost || 0) > 0) extras.push(`Kalıp Bedeli ${fmtTRY(quote.mold_cost)}`);
  if (Number(quote.cliche_cost || 0) > 0) extras.push(`Klişe Bedeli ${fmtTRY(quote.cliche_cost)}`);
  if (Number(quote.extra_shipping_cost || 0) > 0) extras.push(`Nakliye Bedeli ${fmtTRY(quote.extra_shipping_cost)}`);
  if (Number(quote.pallet_cost_per_unit || 0) > 0) {
    const dep = quote.pallet_deposit === false ? "" : " (depozitolu)";
    extras.push(`Palet Bedeli ${fmtTRY(quote.pallet_cost_per_unit)}${dep}`);
  }
  if (extras.length) {
    lines.push("• Ek Kalemler: " + extras.join("; "));
  }
  lines.push(`• GENEL TOPLAM: ${fmtTRY(quote.grand_total)} (KDV hariç)`);
  const _vat = Number(quote.vat_rate || 0);
  if (_vat > 0) {
    const inc = Number(quote.grand_total || 0) * (1 + _vat / 100);
    lines.push(`• KDV DAHİL: ${fmtTRY(inc)} (%${_vat.toLocaleString("tr-TR")} KDV)`);
  }
  // Ödeme koşulları
  const _t = [];
  if (Number(quote.advance_payment_pct || 0) > 0) _t.push(`sipariş anında %${Number(quote.advance_payment_pct)} avans`);
  if (Number(quote.down_payment_pct || 0) > 0) _t.push(`teslim öncesi %${Number(quote.down_payment_pct)} peşinat`);
  const _vd = Number(quote.payment_term_days || 0);
  if (_vd > 0) _t.push(`kalan ${_vd} gün vadeli`);
  if (_t.length) lines.push(`• Ödeme koşulları: ${_t.join(", ")}`);
  lines.push("");
  lines.push("Detaylı teklif PDF ekte tarafınıza iletilmiştir.");
  if (company?.name) {
    lines.push("");
    lines.push(`Saygılarımızla,`);
    lines.push(company.name);
    if (company.phone) lines.push(company.phone);
  }
  return lines.join("\n");
}
